/**
 * Setup Token servisi.
 * Personel telefonuna PWA kurulumu için tek kullanımlık token üretir.
 * 48 saat geçerli (KARAR/spec madde 5.2).
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  getDocs,
  query,
  where,
  Timestamp,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import type { SetupToken } from '@/types'
import { SETUP_TOKEN_GECERLILIK_SAAT } from '@/types'

const TOKENLER = 'setupTokenlari'

/**
 * URL-safe rastgele token üret (~48 karakter, 192-bit güvenlik).
 */
function yeniTokenId(): string {
  const arr = new Uint8Array(24)
  crypto.getRandomValues(arr)
  return Array.from(arr)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

export interface OlusturulanToken {
  tokenId: string
  url: string
  gecerlilikTarihi: Date
}

/**
 * Personel için yeni setup token oluşturur.
 * Eski aktif token varsa iptal eder (kullanıldı=true).
 */
export async function setupTokenOlustur(
  personelId: string,
  olusturanId: string
): Promise<OlusturulanToken> {
  const db = getFirestoreDb()

  // Eski aktif tokenları iptal et
  const eskiQ = query(
    collection(db, TOKENLER),
    where('personelId', '==', personelId),
    where('kullanildi', '==', false)
  )
  const eskiSnap = await getDocs(eskiQ)
  await Promise.all(
    eskiSnap.docs.map(d => updateDoc(d.ref, { kullanildi: true }))
  )

  // Yeni token üret
  const tokenId = yeniTokenId()
  const gecerlilikTarihi = new Date()
  gecerlilikTarihi.setHours(
    gecerlilikTarihi.getHours() + SETUP_TOKEN_GECERLILIK_SAAT
  )

  await setDoc(doc(db, TOKENLER, tokenId), {
    personelId,
    olusturanId,
    olusturmaTarihi: nowServer(),
    gecerlilikTarihi: Timestamp.fromDate(gecerlilikTarihi),
    kullanildi: false,
  })

  // Setup URL — personel bunu QR olarak okutacak (Faz 1.4'te aktif olacak)
  const origin = window.location.origin
  const url = `${origin}/kurulum?token=${tokenId}`

  return { tokenId, url, gecerlilikTarihi }
}

export async function setupTokenGetir(tokenId: string): Promise<SetupToken | null> {
  const db = getFirestoreDb()
  const docSnap = await getDoc(doc(db, TOKENLER, tokenId))
  if (!docSnap.exists()) return null
  return { id: docSnap.id, ...docSnap.data() } as SetupToken
}
